import React, { useEffect, useState, useMemo } from 'react';
import { Select, Row, Col, Input, message, Statistic } from 'antd';
import { TAnimateurInfo, TDashboard } from '../../../models/user/Dashboard';
import { TSubject, TCourse, TChapter, TEpic } from '../../../models/courses/course';
import { useCourseApi } from '../../../api/courses/courses';
import AnimateurContent from './AnimatorContent';
import { TLesson } from '../../../models/courses/lesson';
import { useUserApi } from '../../../api/user/userApp';

const { Option } = Select;

interface DashboardAnimateurProps {
    dashboard: TDashboard | null;
}

// Fonction utilitaire pour regrouper les données par étudiant
const groupByUser = (data: TAnimateurInfo[]) => {
    const grouped = new Map<number, { user: TAnimateurInfo; subjects: Set<number>; courses: Set<number> }>();

    data.forEach((entry) => {
        if (!grouped.has(entry.user_id)) {
            grouped.set(entry.user_id, {
                user: entry,
                subjects: new Set(),
                courses: new Set(),
            });
        }
        const userGroup = grouped.get(entry.user_id)!;
        if (entry.subject_id) userGroup.subjects.add(entry.subject_id);
        if (entry.course_id) userGroup.courses.add(entry.course_id);
    });

    return Array.from(grouped.values());
};

const DashboardAnimateur: React.FC<DashboardAnimateurProps> = ({ dashboard }) => {
    const { getSubjectsActif, getCourses, getAllLessons, getAllEpics, getAllChapters } = useCourseApi();
    const { getUserDetails } = useUserApi();
    const [subjects, setSubjects] = useState<TSubject[]>([]);
    const [courses, setCourses] = useState<TCourse[]>([]);
    const [chapters, setChapters] = useState<TChapter[]>([]);
    const [epics, setEpics] = useState<TEpic[]>([]);
    const [lessons, setLessons] = useState<TLesson[]>([]);
    const [searchValue, setSearchValue] = useState<string>('');
    const [selectedSubject, setSelectedSubject] = useState<number | null>(null);
    const [selectedCourse, setSelectedCourse] = useState<number | null>(null);
    const [activeUsersMap, setActiveUsersMap] = useState<Map<number, boolean>>(new Map());


    // Charger les données au montage
    useEffect(() => {
        Promise.all([
            getSubjectsActif(),
            getCourses(),
            getAllChapters(),
            getAllEpics(),
            getAllLessons(),
        ])
            .then(([subjectsResp, coursesResp, chaptersResp, epicsResp, lessonsResp]) => {
                setSubjects(subjectsResp.data);
                setCourses(coursesResp.data);
                setChapters(chaptersResp.data);
                setEpics(epicsResp.data);
                setLessons(lessonsResp.data);
            })
            .catch((err) => message.error(err.response?.data ?? err.message));
        // eslint-disable-next-line 
    }, []);

    // Regrouper les données par utilisateur
    const groupedData = React.useMemo(() => {
        return dashboard?.dashboard_animateur
            ? groupByUser(dashboard.dashboard_animateur)
            : [];
    }, [dashboard]);

    // Charger l'état d'activation des utilisateurs référencés dans groupedData
    useEffect(() => {
        let cancelled = false;
        const loadActifStates = async () => {
            try {
                const uniqueIds = Array.from(new Set(groupedData.map(g => g.user.user_id)));
                if (uniqueIds.length === 0) {
                    setActiveUsersMap(new Map());
                    return;
                }
                // Ne pas refetch pour les ids déjà connus
                const idsToFetch = uniqueIds.filter(id => !activeUsersMap.has(id));
                if (idsToFetch.length === 0) return;

                const results = await Promise.allSettled(idsToFetch.map(id => getUserDetails(id)));
                if (cancelled) return;

                const nextMap = new Map(activeUsersMap);
                results.forEach((res, idx) => {
                    const id = idsToFetch[idx];
                    if (res.status === 'fulfilled') {
                        const actif = res.value.data?.actif;
                        // Par défaut, on considère actif si non défini
                        nextMap.set(id, actif !== false);
                    } else {
                        // En cas d'échec, considérer actif pour ne pas masquer par erreur
                        nextMap.set(id, true);
                    }
                });
                setActiveUsersMap(nextMap);
            } catch (err: any) {
                // Eviter de bloquer l'affichage; notifier l'erreur
                message.error(err.response?.data ?? err.message);
            }
        };
        loadActifStates();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupedData]);

    // Gestion des filtres
    const filteredSubjects = React.useMemo(() => {
        if (selectedCourse) {
            const course = courses.find((c) => c.id === selectedCourse);
            return course ? subjects.filter((s) => s.id === course.subject_id) : [];
        }
        return subjects;
    }, [selectedCourse, courses, subjects]);

    const filteredCourses = React.useMemo(() => {
        if (selectedSubject) {
            return courses.filter((course) => course.subject_id === selectedSubject);
        }
        return courses;
    }, [selectedSubject, courses]);

    const handleSubjectChange = (value: number | null) => {
        setSelectedSubject(value);
        if (!value) setSelectedCourse(null); // Réinitialise le filtre des cours si le filtre des parcours est désélectionné
    };

    const handleCourseChange = (value: number | null) => {
        setSelectedCourse(value);
        if (value) {
            const course = courses.find((c) => c.id === value);
            if (course) setSelectedSubject(course.subject_id); // Sélection automatique du parcours lié au cours
        }
    };

    // Filtrage des données utilisateur
    const filteredData = React.useMemo(() => {
        // Filtrer les utilisateurs désactivés (actif === false)
        let data = groupedData.filter(group => {
            const actif = activeUsersMap.get(group.user.user_id);
            // Par défaut, afficher si indéterminé
            return actif !== false;
        });

        if (searchValue) {
            data = data.filter((group) =>
                `${group.user.lastname} ${group.user.firstname}`
                    .toLowerCase()
                    .includes(searchValue.toLowerCase())
            );
        }

        return data;
    }, [groupedData, activeUsersMap, searchValue]);

    const palette = useMemo(
        () => ({
            primary: '#2D9CC9',
            secondary: '#8BBE1E',
            background: 'linear-gradient(135deg, rgba(45,156,201,0.08), rgba(139,190,30,0.04))',
            border: 'rgba(45,156,201,0.18)',
            muted: 'rgba(12, 24, 42, 0.65)',
        }),
        []
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16,
                    padding: 20,
                    borderRadius: 28,
                    background: palette.background,
                    border: `1px solid ${palette.border}`,
                    boxShadow: '0 18px 38px rgba(7, 24, 38, 0.12)',
                }}
            >
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} md={10}>
                        <Input.Search
                            placeholder="Rechercher un étudiant"
                            onSearch={(value) => setSearchValue(value)}
                            allowClear
                            style={{ width: '100%' }}
                            size="large"
                        />
                    </Col>
                    <Col xs={24} md={7}>
                        <Select
                            placeholder="Choisir un parcours"
                            style={{ width: '100%' }}
                            onChange={handleSubjectChange}
                            allowClear
                            value={selectedSubject ?? undefined}
                            size="large"
                        >
                            {filteredSubjects.map((subject) => (
                                <Option key={subject.id} value={subject.id}>
                                    {subject.title}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} md={7}>
                        <Select
                            placeholder="Choisir un cours"
                            style={{ width: '100%' }}
                            onChange={handleCourseChange}
                            allowClear
                            value={selectedCourse ?? undefined}
                            size="large"
                        >
                            {filteredCourses.map((course) => (
                                <Option key={course.id} value={course.id}>
                                    {course.title}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={12} md={6}>
                        <Statistic
                            title="Étudiants suivis"
                            value={filteredData.length}
                            valueStyle={{ color: palette.primary }}
                        />
                    </Col>
                    <Col xs={12} md={6}>
                        <Statistic
                            title="Parcours actifs"
                            value={subjects.length}
                            valueStyle={{ color: palette.secondary }}
                        />
                    </Col>
                </Row>
            </div>
            <AnimateurContent
                userData={filteredData}
                subjects={subjects}
                courses={courses}
                chapters={chapters}
                epics={epics}
                lessons={lessons}
                selectedSubject={selectedSubject}
                selectedCourse={selectedCourse}
                palette={palette}
            />
        </div>
    );
};

export default DashboardAnimateur;
